import { useState, useEffect } from 'react';
import { STORAGE_KEYS } from '../constants';
import { verifyAdminAccess, hasAdminAccess } from '../services/firebaseService';

/**
 * Hook for admin authentication
 * Manages authentication state and provides login/logout functions
 * Only users excluded from analytics (test/dev/prod users and PauluneMoon) can access admin
 * @returns {Object} - { isAuthenticated, isLoading, user, login, logout }
 */
export const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is already "logged in"
    const checkAuth = async () => {
      const localAuth = localStorage.getItem(STORAGE_KEYS.AUTH);
      const storedEmail = localStorage.getItem(STORAGE_KEYS.ADMIN_EMAIL);
      
      if (localAuth === 'true' && storedEmail) {
        console.log('[Admin Auth] Checking stored authentication for:', storedEmail);
        
        // First do a quick check - if email matches directly, allow immediately
        if (hasAdminAccess(storedEmail)) {
          console.log('[Admin Auth] Email matches admin criteria, allowing access');
          setIsAuthenticated(true);
          setUser({ email: storedEmail, displayName: 'Admin User' });
          setIsLoading(false);
          return;
        }
        
        // Email doesn't match directly - verify in Firebase (like we do during login)
        console.log('[Admin Auth] Email does not match directly, verifying in Firebase...');
        try {
          const verification = await verifyAdminAccess(storedEmail, true);
          
          if (verification.hasAccess) {
            console.log('[Admin Auth] Stored authentication verified, allowing access');
            setIsAuthenticated(true);
            const displayName = verification.userData?.name || storedEmail.split('@')[0];
            setUser({ email: storedEmail, displayName, userData: verification.userData });
          } else {
            console.log('[Admin Auth] Stored authentication invalid, clearing...');
            // Clear invalid auth
            localStorage.removeItem(STORAGE_KEYS.AUTH);
            localStorage.removeItem(STORAGE_KEYS.ADMIN_EMAIL);
          }
        } catch (error) {
          console.error('[Admin Auth] Error verifying stored authentication:', error);
          // On error, clear auth to be safe
          localStorage.removeItem(STORAGE_KEYS.AUTH);
          localStorage.removeItem(STORAGE_KEYS.ADMIN_EMAIL);
        }
      }
      
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  /**
   * Login function
   * Verifies that the email has admin access (only test/dev/prod users and PauluneMoon)
   * @param {string} email - Admin email address
   * @param {boolean} verifyInFirebase - Whether to verify user exists in Firebase (default: true)
   * @returns {Promise<Object>} - { success: boolean, error?: string }
   */
  const login = async (email, verifyInFirebase = true) => {
    console.log('[Admin Auth] Login attempt with email:', email);
    
    // Validate email format
    if (!email || typeof email !== 'string' || !email.includes('@')) {
      console.log('[Admin Auth] Invalid email format');
      return { success: false, error: 'Please enter a valid email address' };
    }
    
    // Verify admin access
    console.log('[Admin Auth] Verifying admin access...');
    const verification = await verifyAdminAccess(email, verifyInFirebase);
    console.log('[Admin Auth] Verification result:', verification);
    
    if (!verification.hasAccess) {
      console.log('[Admin Auth] Login failed - access denied');
      return { success: false, error: verification.error || 'Access denied. Only authorized admin users can access the dashboard.' };
    }
    
    // Store authentication
    console.log('[Admin Auth] Access granted, storing authentication...');
    localStorage.setItem(STORAGE_KEYS.AUTH, 'true');
    localStorage.setItem(STORAGE_KEYS.ADMIN_EMAIL, email);
    setIsAuthenticated(true);
    
    // Set user data
    const displayName = verification.userData?.name || email.split('@')[0];
    setUser({ email, displayName, userData: verification.userData });
    console.log('[Admin Auth] Login successful for:', email);
    
    return { success: true };
  };

  /**
   * Logout function
   * @returns {Promise<Object>} - { success: boolean }
   */
  const logout = async () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH);
    localStorage.removeItem(STORAGE_KEYS.ADMIN_EMAIL);
    setIsAuthenticated(false);
    setUser(null);
    return { success: true };
  };

  return {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout
  };
};
